import { runInAction, toJS } from 'mobx';
import moment from 'moment';
import Configuracao, { TIPO_ARMAZENAMENTO_LOCAL } from '../util/Configuracao';
import NomeUtil from '../util/NomeUtil';
import StringUtil from '../util/StringUtil';
import Service from './Service';
import ErrorUtil from '@/util/ErrorUtil';

const MSG_ERRO_RECUPERAR_CERTIDAO = 'Falha ao recuperar rascunho da certidão.';

/**
 * Classe utilizada para tratar regras de negócio e manipular o retorno dos webservices
 */
class CertidaoService extends Service {
    async removerAssinaturaCertidao(idSessao, processo) {
        await this.getCertidaoApi().removerAssinaturaCertidao(idSessao, processo);
    }

    async recuperarRascunhoCertidao(registro) {
        let result = await this.getCertidaoApi().recuperarRascunhoCertidao(
            registro.sessaoId,
            registro.processo.numeroCompleto
        );

        result = await this.tratarResposta(result);

        // Trata o texto do rascunho apenas se a certidão não tiver sido gerada
        if (!registro.certidaoGerada) {
            result = StringUtil.tratarCaracteresNaoLatin1(result);
        }

        return result;
    }

    async tratarResposta(result) {
        if (typeof result === 'undefined') {
            throw new Error(MSG_ERRO_RECUPERAR_CERTIDAO);
        }

        if (typeof result === 'number' || typeof result === 'bigint') {
            return '' + result;
        }

        if (typeof result !== 'string') {
            const MSG_ERRO_ESPECIFICA = MSG_ERRO_RECUPERAR_CERTIDAO + ' [resposta inválida]';
            throw new Error(MSG_ERRO_ESPECIFICA);
        }

        return result;
    }

    salvarHistoricoRascunhoCertidao(registro, rascunho) {
        if (Configuracao.HISTORICO_RASCUNHO_CERTIDAO_TIPO_ARMAZENAMENTO === TIPO_ARMAZENAMENTO_LOCAL.localStorage) {
            let storageName = `${Configuracao.HISTORICO_RASCUNHO_CERTIDAO_PREFIXO_STORAGES + registro.sessaoId}_${registro.processo.numeroCompleto}_${moment().format(Configuracao.HISTORICO_RASCUNHO_CERTIDAO_FORMATO_DATAHORA)}`;
            localStorage.setItem(storageName, rascunho);
        }

        if (Configuracao.HISTORICO_RASCUNHO_CERTIDAO_TIPO_ARMAZENAMENTO === TIPO_ARMAZENAMENTO_LOCAL.IndexedDB) {
            // TODO SE-8374
        }
    }

    /**
     * Exclui os históricos de rascunhos de certidão antigos.
     *
     * @param {number} tempoExpiracaoMinutos O tempo de expiração dos rascunhos em minutos
     */
    static excluirHistoricosRascunhoCertidao(
        tempoExpiracaoMinutos = Configuracao.HISTORICO_RASCUNHO_CERTIDAO_TEMPO_EXPIRACAO_MINUTOS
    ) {
        const dataHoraReferenciaExpiracao = moment().subtract(tempoExpiracaoMinutos, 'minutes');
        let selecionadosParaExclusao = [];

        if (Configuracao.HISTORICO_RASCUNHO_CERTIDAO_TIPO_ARMAZENAMENTO === TIPO_ARMAZENAMENTO_LOCAL.localStorage) {
            for (let i = 0; i < localStorage.length; i++) {
                const ls = localStorage.key(i);

                if (ls !== null && ls.indexOf(Configuracao.HISTORICO_RASCUNHO_CERTIDAO_PREFIXO_STORAGES) !== -1) {
                    let dataHoraStr = ls.slice(-1 * Configuracao.HISTORICO_RASCUNHO_CERTIDAO_FORMATO_DATAHORA.length);
                    let dataHora = moment(
                        dataHoraStr,
                        Configuracao.HISTORICO_RASCUNHO_CERTIDAO_FORMATO_DATAHORA
                    ).toDate();

                    if (moment(dataHora).isBefore(dataHoraReferenciaExpiracao)) {
                        selecionadosParaExclusao.push(localStorage.key(i));
                    } else {
                        console.info(localStorage.key(i) + ' não será removida.');
                    }
                }
            }

            selecionadosParaExclusao.forEach((element) => {
                if (element !== null && element !== undefined) {
                    console.info(`Removendo ${element}...`);
                    localStorage.removeItem(element);
                }
            });
        }

        if (Configuracao.HISTORICO_RASCUNHO_CERTIDAO_TIPO_ARMAZENAMENTO === TIPO_ARMAZENAMENTO_LOCAL.IndexedDB) {
            // TODO SE-8374
        }

        return selecionadosParaExclusao.length;
    }

    async salvarRascunhoCertidao(registro, rascunho) {
        rascunho = StringUtil.tratarCaracteresNaoLatin1(rascunho);
        this.salvarHistoricoRascunhoCertidao(registro, rascunho);

        if (rascunho === '') {
            await this.getCertidaoApi().deleteRascunhoCertidao(registro.sessaoId, registro.processo.numeroCompleto);
        } else {
            await this.getCertidaoApi().salvarRascunhoCertidao(registro.sessaoId, registro.processo.numeroCompleto, rascunho);
        }

        return rascunho;
    }

    async salvarRascunhoCertidaoEmLote(listaProcessos, rascunho, operacaoDecisao, operacaoObservacao) {
        rascunho = StringUtil.tratarCaracteresNaoLatin1(rascunho);

        try {
            const response = await this.getCertidaoApi().salvarRascunhoCertidaoEmLote(
                listaProcessos,
                rascunho,
                operacaoDecisao,
                operacaoObservacao
            );
            return response.data.map((registro) => {
                if (registro.erro) {
                    return {
                        ...registro,
                        mensagem: registro.mensagens && registro.mensagens.length > 0 ? registro.mensagens[0] : ''
                    };
                } else {
                    return registro;
                }
            });
        } catch (error) {
            const errors = listaProcessos.map((registro) => {
                return ErrorUtil.getResultadoProcessamentoErro(error, registro.numeroProcesso);
            });
            return errors;
        }
    }

    salvarResumo = (registro, resumo) => {
        resumo = StringUtil.tratarCaracteresNaoLatin1(resumo);

        if (resumo === '') {
            this.getCertidaoApi().excluirResumo(registro.sessaoId, registro.processo.numeroCompleto);
        } else {
            this.getCertidaoApi().salvarResumo(registro.sessaoId, registro.processo.numeroCompleto, resumo);
        }

        return resumo;
    };

    salvarDecisao = (registro, decisao) => {
        decisao = StringUtil.tratarCaracteresNaoLatin1(decisao);

        if (decisao === '') {
            this.getCertidaoApi().excluirDecisao(registro.sessaoId, registro.processo.numeroCompleto);
        } else {
            this.getCertidaoApi().salvarDecisao(registro.sessaoId, registro.processo.numeroCompleto, decisao);
        }

        return decisao;
    };

    salvarPartes = (registro, partes) => {
        partes = StringUtil.tratarCaracteresNaoLatin1(partes);

        if (partes === '') {
            this.getCertidaoApi().excluirPartes(registro.sessaoId, registro.processo.numeroCompleto);
        } else {
            this.getCertidaoApi().salvarPartes(registro.sessaoId, registro.processo.numeroCompleto, partes);
        }

        return partes;
    };

    salvarCpfSignatario = (registro, cpfSignatario) => {
        return this.getCertidaoApi().salvarCpfSignatario(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            cpfSignatario
        );
    };

    async salvarRedator(registro, redator) {
        return this.getCertidaoApi().salvarRedator(registro.sessaoId, registro.processo.numeroCompleto, redator);
    }

    async removerRedator(registro) {
        return this.getCertidaoApi().removerRedator(registro.sessaoId, registro.processo.numeroCompleto);
    }

    recuperarCamposCertidao(registro, siglaOJC, nomeOJC, tipoJulgamentoSessao) {
        return this.getCertidaoApi().recuperarCamposCertidao(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            siglaOJC,
            nomeOJC,
            tipoJulgamentoSessao,
            registro.tipoJulgamento
        );
    }

    async montarBodyCertidao(parametrosCertidao) {
        let sessao = parametrosCertidao.sessao;
        let registro = parametrosCertidao.registro;
        let textoPartes = parametrosCertidao.textoPartes;
        let composicao = parametrosCertidao.composicao;

        if (!composicao) {
            composicao = registro.composicao
                ? registro.composicao.map((comp) => toJS(comp.magistrado))
                : sessao.composicaoPadrao.map((comp) => toJS(comp.titular));
        }

        let procurador = await this.getProcuradorCertidao(parametrosCertidao, registro);

        runInAction(() => {
            if (procurador) {
                procurador.tipo = procurador.tipo ? procurador.tipo : 'PROCURADOR';
            } else {
                procurador = undefined;
            }
        });

        textoPartes = textoPartes ? textoPartes : this.getStringPartes(registro);

        return {
            modeloSemDecisao: parametrosCertidao.certidaoSemModelo,
            siglaOrgaoJulgadorColegiado: sessao.orgaoJudicante.sigla,
            nomeOrgaoJulgadorColegiado: toJS(sessao.orgaoJudicante.descricao),
            idOrgaoJulgadorColegiado: sessao.orgaoJudicante.id,
            resumoSessao: {
                tipoJulgamentoSessao: sessao.tipoJulgamento,
                composicao: composicao,
                procurador: procurador,
                presidente: composicao.find((comp) => comp.presidenteSessao),
                relator: this.getMagistradoComposicao(composicao, registro.processo.relator),
                redator: this.getMagistradoComposicao(composicao, registro.processo.redator),
                revisor: this.getMagistradoComposicao(composicao, registro.processo.revisor),
                impedimentos: registro.impedimentos || [],
                tipoSessao: sessao.tipo,
                dataInicioVotacaoVirtual: sessao.prazos.dataInicioVotacaoVirtual,
                dataFimVotacaoVirtual: sessao.prazos.dataFimVotacaoVirtual,
                dataFimVotacaoVirtualExibicao: sessao.prazos.dataFimVotacaoVirtualExibicao,
                dataInicioSessaoPresencial: sessao.prazos.dataInicioSessaoPresencial
            },
            antiguidades: {
                mapa: sessao.antiguidade
            },
            textoPartes: textoPartes,
            textoDecisao: parametrosCertidao.textoDecisao,
            cpfSignatario: parametrosCertidao.cpfSignatario,
            tipoJulgamento: registro.tipoJulgamento
        };
    }

    async getProcuradorCertidao(parametrosCertidao, registro) {
        return (
            parametrosCertidao.procurador ||
            registro.procurador ||
            (await this.getSessaoApi().recuperarProcurador(registro.sessaoId))
        );
    }

    async criarCertidaoPadrao(
        sessao,
        registro,
        textoDecisao,
        textoPartes,
        cpfSignatario,
        procurador,
        certidaoSemModelo
    ) {
        let body = await this.montarBodyCertidao({
            sessao: sessao,
            registro: registro,
            textoDecisao: textoDecisao,
            textoPartes: textoPartes,
            cpfSignatario: cpfSignatario,
            procurador: procurador,
            certidaoSemModelo: certidaoSemModelo
        });
        let retorno = await this.getCertidaoApi().criarCertidao(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            body
        );
        return retorno.data;
    }

    async gerarCertidaoLote(sessao, registro, textoDecisao, cpfSignatario, certidaoSemModelo) {
        let body = await this.montarBodyCertidao({
            sessao: sessao,
            registro: registro,
            textoDecisao: textoDecisao,
            cpfSignatario: cpfSignatario,
            certidaoSemModelo: certidaoSemModelo
        });
        return this.processarOperacaoLote(
            () => this.getCertidaoApi().criarCertidao(registro.sessaoId, registro.processo.numeroCompleto, body, true),
            registro
        );
    }

    async atualizarResumoCertidao(sessao, registro, procurador, composicao) {
        let body = await this.montarBodyCertidao({
            sessao: sessao,
            registro: registro,
            textoDecisao: ' ',
            textoPartes: ' ',
            procurador: procurador,
            composicao: composicao
        });
        return this.getCertidaoApi().atualizarResumo(registro.sessaoId, registro.processo.numeroCompleto, body);
    }

    async salvarPresidente(registro, presidente, cargoSignatario) {
        return this.getCertidaoApi().salvarPresidente(registro.sessaoId, registro.processo.numeroCompleto, presidente, cargoSignatario);
    }

    async removerPresidente(registro) {
        await this.getCertidaoApi().removerPresidente(
            registro.sessaoId,
            registro.processo.numeroCompleto
        );

        runInAction(() => {
            registro.presidenteSessaoJulgamento = null;
        });
    }

    getMagistradoComposicao(composicao, papel) {
        if (papel && papel.sigla) {
            let magistradoComposicao = composicao.find((comp) => comp.sigla === papel.sigla);

            if (magistradoComposicao) {
                if (magistradoComposicao.ausente === undefined) {
                    magistradoComposicao.ausente = false;
                }

                return toJS(magistradoComposicao);
            }
        }
    }

    async transferirCertidao(sessao, processos) {
        return this.getCertidaoApi().transferirCertidao(sessao, processos);
    }

    getStringPartes = (registro) => {
        if (registro && registro.detalhes && registro.detalhes.partesProcessuais) {
            let partes = '';
            let mostrarAdvogados = this.mostrarAdvogadoDasPartesNaCertidao(registro);
            for (let partesProcesso of registro.detalhes.partesProcessuais) {
                partes += this.getDenominacaoNomePartes(partesProcesso, registro);
                if (partesProcesso.partes.length > 1) {
                    partes = partes.replace(/, ([^,]*)$/, ' e ' + '$1');
                }
                if (mostrarAdvogados) {
                    partes += this.getTextoAdvogados(partesProcesso);
                }
                partes += '\n';
            }
            return partes;
        }
        return '<PROCESSO SEM PARTES>';
    };

    getDenominacaoNomePartes(partesProcesso, registro) {
        let partes = '';
        let arrayPartes = partesProcesso.partes ? partesProcesso.partes.slice() : [];

        for (let index in arrayPartes) {
            let parte = partesProcesso.partes[index];

            if (parseInt(index) > 0) {
                partes += ', ';
            } else {
                partes += partesProcesso.denominacao + ': ';
            }

            if (registro.segredoJustica) {
                partes += NomeUtil.abreviarNome(parte.parte.nome);
            } else {
                partes += parte.parte.nome;
            }
        }

        return partes;
    }

    mostrarAdvogadoDasPartesNaCertidao(registro) {
        return registro.informacoesAdicionais && registro.informacoesAdicionais.agravoProvido === true;
    }

    getTextoAdvogados(partesProcesso) {
        let textoAdvogados = partesProcesso.partes
            .filter((parte) => parte.representantes)
            .reduce((texto, parte, index) => {
                const textoAdvogadosDaParte = this.getAdvogadosDaParte(parte);
                if (textoAdvogadosDaParte) {
                    return `${index === 0 ? texto : texto + ', '}${textoAdvogadosDaParte}`;
                } else {
                    return texto;
                }
            }, '')
            .replace(/, ([^,]*)$/, ' e ' + '$1');
        if (textoAdvogados !== '') {
            textoAdvogados = textoAdvogados.replace(
                /^/,
                `\nAdvogado(s) do(s) ${partesProcesso.denominacao.toLowerCase()}: `
            );
        }
        return textoAdvogados;
    }

    getAdvogadosDaParte(parte) {
        return parte.representantes.reduce(
            (texto, advogado, index) => `${index === 0 ? texto : texto + ', '}${advogado.nome}`,
            ''
        );
    }
}

export default CertidaoService;
